import React, { useCallback, useState, useEffect } from "react";
import styled, { withTheme } from "styled-components";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import PropTypes from "prop-types";
import { profiles, Sites } from "../../../modules/constants";
import { useTranslation } from "react-i18next";
import { isEmailValid } from "../../../modules/utils";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FieldTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-weight: 600;
  font-size: 16px;
`;

const CheckboxTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
  margin-top: 20px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const AddNonParticipant = (props) => {
  const {
    sites,
    roles,
    onSubmit,
    hasError,
    onValidateEmail,
    emailExists,
    resetEmailExists,
  } = props;

  const { t } = useTranslation();

  const [site, setSite] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(null);

  const [errors, setErrors] = useState({});

  const [submitting, setSubmitting] = useState(false);

  const [availableSites, setAvailableSites] = useState([]);

  const selectSite = [
    profiles.dietitian,
    profiles.siteCoordinator,
    profiles.pi,
  ];

  useEffect(() => {
    if (role) {
      validadeRole();

      setErrors((prevState) => ({
        ...prevState,
        site: undefined,
      }));

      setSite(null);

      if (
        [
          profiles.siteMonitor,
          profiles.studyAdmin,
          profiles.sponsor,
          profiles.contentAdmin,
          profiles.medicalMonitor,
          profiles.dataManager,
        ].includes(role)
      ) {
        setAvailableSites([Sites.ALL]);
        setSite(Sites.ALL);
      } else if (
        [profiles.dietitian, profiles.siteCoordinator, profiles.pi].includes(
          role
        )
      ) {
        setAvailableSites(sites);
      }
    }
  }, [role, sites]);

  useEffect(() => {
    if (hasError) {
      setSubmitting(false);
    }
  }, [hasError]);

  const isSiteValid = useCallback(() => {
    return !!site;
  }, [site]);

  const isRoleValid = useCallback(() => {
    return !!role;
  }, [role]);

  const validadeEmail = useCallback(
    (value) => {
      setErrors((prevState) => ({
        ...prevState,
        email: isEmailValid(value)
          ? null
          : t("errorText.addNonParticipant.email.valid"),
      }));
    },
    [isEmailValid, t]
  );

  const validadeSite = useCallback(() => {
    setErrors((prevState) => ({
      ...prevState,
      site: isSiteValid() ? undefined : t("errorText.addNonParticipant.site"),
    }));
  }, [isSiteValid, t]);

  const validadeRole = useCallback(() => {
    setErrors((prevState) => ({
      ...prevState,
      role: isRoleValid() ? undefined : t("errorText.addNonParticipant.role"),
    }));
  }, [isRoleValid, t]);

  const onEmailChange = (value) => {
    resetEmailExists();
    validadeEmail(value);
    setEmail(value);
  };

  const submitHandle = useCallback(() => {
    validadeEmail(email);
    validadeSite();
    validadeRole();

    if (isEmailValid(email) && isSiteValid() && isRoleValid()) {
      const formData = {
        email: email,
        siteIds: site === Sites.ALL ? [] : [site.id],
        role: role,
      };

      setSubmitting(true);
      onSubmit(formData);
    }
  }, [
    email,
    isEmailValid,
    isRoleValid,
    isSiteValid,
    onSubmit,
    role,
    site,
    validadeEmail,
    validadeRole,
    validadeSite,
  ]);

  useEffect(() => {
    if (site) {
      validadeSite();
    }
  }, [site]);

  useEffect(() => {
    if (emailExists) {
      setErrors((prevState) => ({
        ...prevState,
        email: "The email address you have entered is already registered.",
      }));
    }
  }, [emailExists, email]);

  return (
    <Container data-testid="add-non-participant">
      <LabelledInput
        data-testid="email-input"
        title={t("appText.nonParticipants.modal.add.email")}
        value={email}
        type="text"
        hasError={errors?.email}
        errorMessage={errors?.email}
        onChange={(e) => onEmailChange(e.target.value)}
        onBlur={() => onValidateEmail(email)}
        pattern=".{0,128}"
        autoFocus={true}
      />

      <Container className="mt-4">
        <FieldTitle>{t("appText.nonParticipants.modal.add.role")}</FieldTitle>
        {roles?.map((item) => (
          <Row className="mt-2" key={`key_${item}`}>
            <Checkbox
              data-testid={`checkbox_role_${item}`}
              checked={role === item}
              icon={false}
              square={false}
              round={true}
              onClick={() => setRole(item)}
            />
            <CheckboxTitle
              data-testid={`checkbox_title_role_${item}`}
              className="ml-3"
              onClick={() => setRole(item)}
            >
              {item}
            </CheckboxTitle>
          </Row>
        ))}
        <StyledError>{errors?.role && <span>{errors?.role}</span>}</StyledError>
      </Container>
      {availableSites?.length > 0 && selectSite.includes(role) && (
        <Container className="mt-4">
          <Row>
            <FieldTitle className="mr-2">{`${t(
              "appText.nonParticipants.modal.add.site"
            )}:`}</FieldTitle>
            <CustomDropdown
              placeholder={
                site?.value ?? t("appText.nonParticipants.modal.add.select")
              }
              values={availableSites}
              width={145}
              onChange={(value) => setSite(value)}
              limitheight={true}
            />
          </Row>
          <StyledError>
            {errors?.site && <span>{errors?.site}</span>}
          </StyledError>
        </Container>
      )}

      {onSubmit && (
        <ActionButtonsContainer>
          <PrimaryButton
            data-testid="submit-button"
            disabled={emailExists || submitting}
            onClick={submitHandle}
          >
            {t("appText.nonParticipants.modal.add.sendInvite")}
          </PrimaryButton>
        </ActionButtonsContainer>
      )}
    </Container>
  );
};

AddNonParticipant.defaultProps = {
  sites: [],
  roles: [],
};

AddNonParticipant.propTypes = {
  sites: PropTypes.array,
  roles: PropTypes.array,
};

export default withTheme(AddNonParticipant);
